import React from "react";
import { useTranslation } from "react-i18next";
import { useGlobal } from "reactn";
import { MediaQuery } from "@outplayed/responsive";
import { SummonerSpell } from "@outplayed/tooltips";
import SkillPath from "@ugg/shared/components/common/Skills/SkillPath";
import ChampionSkillWithLabel from "@ugg/shared/components/common/Skills/ChampionSkillWithLabel";
import { HeaderBullet } from "@ugg/shared/components/common/HeaderBullet";
import { ReactComponent as ArrowRight } from "@ugg/shared/assets/svg/arrow-right.svg";
import { OverviewInfo } from "@ugg/shared/api/data-parser/champions/overview";

// ─────────────────────────────────────────────────────────────────────
// Skill Priority / Summoner Spells / Skill Path — ARAM Mayhem's own copy.
//
// A deliberate sibling of Overview/Skills.tsx + Overview/SummonerSpells.tsx
// rather than a reuse of them, for two reasons:
//
//  1. NO STATS. Mayhem publishes no build data, so this renders ARAM's picks.
//     ARAM's win rates and match counts describe a different game mode and
//     must never appear on a Mayhem surface. Threading a `hideStats` prop
//     through the shared modules would put that rule in code six other build
//     pages run through; here it is simply not implemented.
//  2. NO SHARED STYLESHEET. Every rule for `.recommended-build_skills` and
//     `.summoner-spells` is nested under `.champion-recommended-build` in
//     three stylesheets (shared, website, monoapp). Reusing those modules
//     outside a recommended build means reproducing that scope and then
//     fighting it back with overrides. This module is Tailwind-only, so the
//     page owns its own layout and the build pages' SCSS can change freely.
//
// What IS shared: SkillPath and ChampionSkillWithLabel, whose styles live in
// their own global stylesheets (common/skill-path.scss,
// common/champion-skill-with-label.scss) and are imported by both shells.
// They are primitives, not build-page components.
//
// Summoner spells sit in the Skill Priority column rather than in a module of
// their own: the column is narrow and, with the win rate gone, mostly empty.
// ─────────────────────────────────────────────────────────────────────

// Card surface for each cell. The 1px grid gap over the near-black track
// below draws the divider between columns, so no cell carries a border.
const CELL = "flex flex-col bg-[#191937] p-[12px] xxs:p-[18px]";

interface AramMayhemSkillsProps {
  championId: number;
  skillPriority: NonNullable<OverviewInfo["rec_skills"]>;
  skillPath: OverviewInfo["rec_skill_path"];
  evolutionPath?: OverviewInfo["rec_skill_evolve_path"] | undefined | null;
  summonerSpells: OverviewInfo["rec_summoner_spells"];
}

export function AramMayhemSkills(props: AramMayhemSkillsProps) {
  const { t } = useTranslation();
  const { championId, skillPriority, skillPath, evolutionPath, summonerSpells } = props;

  const skillEvolution = evolutionPath && evolutionPath.slots?.length > 0 && (
    <div className="flex items-center rounded-[3px] bg-[#11112a]">
      <div className="mx-[12px] text-[11px] font-medium text-[#cddcfe]">Evolution Order:</div>
      {evolutionPath.slots.map((skill, index) => (
        <React.Fragment key={index}>
          <ChampionSkillWithLabel
            className="h-[24px] w-[24px] flex-none [&_img]:h-[24px] [&_img]:w-[24px] [&_img]:rounded-[3px]"
            label={skill}
            labelPosition="bottomRight"
            championId={championId}
            skillSlot={skill}
            ssr
          />
          {index !== evolutionPath.slots.length - 1 && <ArrowRight className="mx-[6px] w-[10px]" />}
        </React.Fragment>
      ))}
    </div>
  );

  return (
    // The 1px gap over a near-black track is the divider between the two
    // columns; `overflow-hidden` keeps the track inside the rounded corners.
    // Single column below 900px, where a 202px sidebar leaves the skill path
    // nothing to scroll in.
    <div className="grid grid-cols-1 gap-[1px] overflow-hidden rounded-[3px] bg-[#070720] sm:grid-cols-[202px_1fr]">
      <div className={CELL}>
        <HeaderBullet className="mb-[18px]">{t("Skill Priority")}</HeaderBullet>
        {/* Ability arrows. `justify-start` (not the build page's centering):
            with the win rate gone there is nothing under them to center
            against, and left-aligning lands them under the header's text. */}
        <div className="flex items-center justify-start [&_img]:h-[36px] [&_img]:w-[36px] [&_img]:rounded-[3px]">
          {skillPriority.slots.map((skill, index) => (
            <React.Fragment key={index}>
              <ChampionSkillWithLabel label={skill} labelPosition="bottomCenter" championId={championId} skillSlot={skill} ssr />
              {index !== 2 && <ArrowRight className="mx-[5px] w-[12px]" />}
            </React.Fragment>
          ))}
        </div>
        {summonerSpells && <AramMayhemSummonerSpells summonerSpells={summonerSpells} />}
      </div>
      <div className={CELL}>
        <HeaderBullet className="mb-[18px] flex-wrap gap-y-[6px]">
          <span className="mr-[12px]">{t("Skill Path")}</span>
          <MediaQuery min="TABLET" max="DESKTOP_LARGE">
            <span className="text-[14px] font-normal text-[#5f5f7b]">Most popular ability leveling order</span>
          </MediaQuery>
          {skillEvolution && (
            <MediaQuery min="TABLET" max="DESKTOP_LARGE">
              <div className="ml-auto">{skillEvolution}</div>
            </MediaQuery>
          )}
        </HeaderBullet>
        {skillEvolution && (
          <MediaQuery min="MOBILE_SMALL" max="MOBILE_LARGE">
            <div className="mb-[12px] inline-flex">{skillEvolution}</div>
          </MediaQuery>
        )}
        {/* The path grid is 18 fixed 24px columns wide and does not shrink, so
            it scrolls rather than squashing the labels beside it. */}
        <div className="overflow-x-auto">
          <SkillPath championId={championId} skillOrder={skillPath?.slots || []} skillNames includePassive rec ssr />
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Summoner spells, as a footer inside the Skill Priority column.
//
// `mt-auto` pushes it to the base of the (taller) Skill Path column beside
// it, so the spell icons' bottom edge lands on the last skill-path row.
// `mb-[4px]` backs it off by `.skill-path-container`'s own 4px padding-bottom,
// the trailing space under that column's last row. Anchoring to the bottom
// rather than using a fixed top margin is what keeps this aligned for
// champions with an evolution row (Kai'Sa, Kha'Zix), whose module is taller.
//
// `pt-[18px]` is the floor for the stacked layout, where the column is no
// taller than its content, `mt-auto` collapses to zero, and the spells would
// otherwise butt against the ability arrows.
// ─────────────────────────────────────────────────────────────────────

function AramMayhemSummonerSpells({ summonerSpells }: { summonerSpells: NonNullable<OverviewInfo["rec_summoner_spells"]> }) {
  const { t } = useTranslation();
  const [language] = useGlobal("language");

  // `ids` can carry a 0 placeholder when the pipeline has no second spell for
  // a champion; 0 is not a summoner spell id and renders a broken icon.
  const spellIds = (summonerSpells.ids || []).filter((id) => id > 0);
  if (spellIds.length === 0) return null;

  return (
    <div className="mt-auto mb-[4px] pt-[18px]">
      <HeaderBullet className="mb-[18px]">{t("Summoner Spells")}</HeaderBullet>
      <div className="flex gap-[12px] [&_img]:h-[36px] [&_img]:w-[36px] [&_img]:rounded-[3px]">
        {spellIds.map((spellId) => (
          <SummonerSpell key={spellId} spellId={spellId} ssr language={language} webp />
        ))}
      </div>
    </div>
  );
}
